import { Image } from '@chakra-ui/react';
import { Box, Button, Text, theme } from '@everlywell/ui-kit';
import type { EverywhereTest } from 'components/Everywhere/data';
import {
  displayName,
  everywhereProductSlug,
  formatEverywherePrice,
  testPath,
} from 'components/Everywhere/helpers';
import { useEverywherePricing } from 'components/Everywhere/useEverywherePricing';
import PriceV2 from 'components/PricingV2/PriceV2/PriceV2';
import ProductCardV2 from 'components/ProductCardV2';
import { ProductCardBadge } from 'components/ProductDisplayGridV2/ProductDisplayGridV2';
import { navigate } from 'gatsby';
import React, { useMemo } from 'react';
import { trackEva, trackEvaProductClick } from 'utils/eva';
import { QUEST_TAG } from 'utils/helpers/products';

import { sanitizeAgentText } from '../../lib/formatters';
import everywhereLabVisit2 from './everywhere-lab-visit-2.jpg';
import everywhereLabVisit3 from './everywhere-lab-visit-3.jpg';
import everywhereLabVisit4 from './everywhere-lab-visit-4.jpg';
import everywhereLabVisit5 from './everywhere-lab-visit-5.jpg';
import everywhereLabVisit6 from './everywhere-lab-visit-6.jpg';
import everywhereLabVisit from './everywhere-lab-visit.jpg';
import everywhereLockup from './everywhere-lockup.png';

type EverywhereSearchProductCardProps = {
  test: EverywhereTest;
  description: string;
  onCardClick: () => void;
  imageIndex?: number;
  experience?: 'search' | 'conversation';
};

const labVisitImages = [
  everywhereLabVisit,
  everywhereLabVisit2,
  everywhereLabVisit3,
  everywhereLabVisit4,
  everywhereLabVisit5,
  everywhereLabVisit6,
] as const;

type PatientScope = 'female' | 'male' | 'unscoped';

// Keep this aligned with labVisitImages. The scope describes the patient,
// not the clinician.
const labVisitImageScopes: PatientScope[] = [
  'female',
  'female',
  'male',
  'female',
  'male',
  'male',
];

const femaleTestPattern =
  /\b(pregnan\w*|prenatal|maternal|ovarian|ovulation|cervix|cervical|menopaus\w*|menstrual|women[’']?s fertility|female fertility)\b/i;
const maleTestPattern =
  /\b(vasectomy|prostate|psa|semen|testicular|men[’']?s fertility|male fertility)\b/i;

const getPatientScope = (testName: string): PatientScope => {
  if (femaleTestPattern.test(testName)) return 'female';
  if (maleTestPattern.test(testName)) return 'male';
  return 'unscoped';
};

/**
 * Assign all Everywhere images for a results page together. Scoped tests get
 * first choice from the matching patient images, then unscoped tests consume
 * the remaining images. Since a page contains at most six cards, no image is
 * repeated.
 */
export const assignEverywhereLabVisitImages = (
  testNames: Array<string | null>,
  page: number,
): Array<number | undefined> => {
  const available = Array.from(
    { length: labVisitImages.length },
    (_, index) => (index + page) % labVisitImages.length,
  );
  const assignments: Array<number | undefined> = testNames.map(() => undefined);

  const assignScope = (scope: PatientScope) => {
    testNames.forEach((testName, resultIndex) => {
      if (!testName || getPatientScope(testName) !== scope) return;

      const availableIndex =
        scope === 'unscoped'
          ? 0
          : available.findIndex(
              (imageIndex) => labVisitImageScopes[imageIndex] === scope,
            );
      if (availableIndex < 0) return;

      assignments[resultIndex] = available.splice(availableIndex, 1)[0];
    });
  };

  assignScope('female');
  assignScope('male');
  assignScope('unscoped');

  // Preserve the no-repeat guarantee if a future page contains more than
  // three explicitly scoped tests for the same patient group.
  testNames.forEach((testName, resultIndex) => {
    if (testName && assignments[resultIndex] === undefined) {
      assignments[resultIndex] = available.shift();
    }
  });

  return assignments;
};

/**
 * The tile crops the photo differently per breakpoint, so the lockup sits on
 * top of it rather than baked into the photo, where the crop would clip it.
 * At the mobile tile size it would render a few pixels tall, so it is dropped
 * there and the "in-person" badge carries the meaning instead.
 */
function LabVisitImage({ imageIndex }: { imageIndex: number }): JSX.Element {
  const image = labVisitImages[imageIndex % labVisitImages.length];

  return (
    <Box position="relative" width="100%" height="100%">
      <Image
        src={image}
        alt="In-person lab visit"
        position="absolute"
        inset={0}
        width="100%"
        height="100%"
        objectFit="cover"
      />
      <Image
        src={everywhereLockup}
        alt=""
        display={['none', 'block']}
        position="absolute"
        bottom="10px"
        left="50%"
        transform="translateX(-50%)"
        width="66%"
        maxWidth="180px"
        filter="drop-shadow(0 2px 6px rgba(0, 0, 0, 0.18))"
      />
    </Box>
  );
}

export default function EverywhereSearchProductCard({
  test,
  description,
  onCardClick,
  imageIndex = 0,
  experience = 'search',
}: EverywhereSearchProductCardProps): JSX.Element | null {
  const { labPriceFor, reviewFee } = useEverywherePricing();
  const name = displayName(test);
  const slug = everywhereProductSlug(test);
  const href = testPath(test.name);
  const price = labPriceFor(test);
  const sanitizedDescription = useMemo(
    () => sanitizeAgentText(description),
    [description],
  );

  const trackClick = (source: 'card' | 'cta') => {
    trackEvaProductClick(slug);
    trackEva('Eva Product Card Clicked', {
      product: {
        slug,
        name,
        price,
        url: href,
      },
      product_type: 'everywhere',
      experience,
      source,
    });
  };

  const handleCardClick = () => {
    trackClick('card');
    onCardClick();
  };

  const handleCtaClick = () => {
    trackClick('cta');
    // Closing the modal first repaints the page behind it before the route
    // swaps, so keep it up until the test page is on screen.
    void navigate(href).then(onCardClick);
  };

  if (price === null || reviewFee === null) return null;

  const dollars = String(Math.round(price));

  return (
    <ProductCardV2
      handleOnClick={handleCardClick}
      linkTo={href}
      linkAriaLabel={`${name}. ${test.desc}`}
      variant="default"
      fullBleedImage
      image={<LabVisitImage imageIndex={imageIndex} />}
      tag={
        <Box display={['none', 'flex']} gap="0.5" alignItems="center">
          <ProductCardBadge>{QUEST_TAG}</ProductCardBadge>
        </Box>
      }
      pricing={
        // ProductCardV2 gives the price row no padding in the mobile row
        // layout, which leaves the review line sitting on the card's edge.
        <Box paddingBottom={[2, 0]}>
          <PriceV2 dollarAmount={dollars} />
          {/* Tight leading keeps this second line from outgrowing the CTA
              button, which would push the button out of line with the
              single-price cards beside it. */}
          <Text fontSize="sm" color="tints.darkGrey" lineHeight="1">
            + {formatEverywherePrice(reviewFee)} review
          </Text>
        </Box>
      }
      cta={
        <Button
          type="button"
          size="sm"
          minWidth={['100px', '120px']}
          height={['32px', '40px']}
          backgroundColor="eggplant.base"
          color="white"
          fontWeight="medium"
          _hover={{ backgroundColor: 'violet.dark' }}
          onClick={handleCtaClick}
        >
          Order this test
        </Button>
      }
    >
      <Box
        display={['flex', 'none']}
        marginBottom={2}
        gap="0.5"
        alignItems="center"
      >
        <ProductCardBadge>{QUEST_TAG}</ProductCardBadge>
      </Box>
      <Text
        fontSize={['lg', '3xl']}
        color="black"
        fontWeight="medium"
        marginY="2"
        textAlign={['left', 'center']}
      >
        {name}
      </Text>
      <Box
        fontSize="sm"
        color={theme.colors.tints.darkGrey}
        textAlign={['left', 'center']}
        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
      />
    </ProductCardV2>
  );
}
